import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  category: string;
  date: string;
  imageUrl: string;
  children: React.ReactNode;
}

export default function ArticleLayout({ title, subtitle, category, date, imageUrl, children }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <Header />

      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors mb-6"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full whitespace-nowrap">
              {category}
            </span>
            <span className="text-xs text-foreground-400">{date}</span>
          </div>

          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
            {title}
          </h1>

          <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
            {subtitle}
          </p>

          <div className="w-full aspect-[21/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 space-y-5 leading-relaxed">
            {children}
          </div>

          <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-xl">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
              Ready to Start Shopping?
            </h3>
            <p className="text-sm text-foreground-600 mb-5">
              Explore our curated Sugargoo spreadsheet database to find the best products across all categories — from sneakers and streetwear to accessories and everyday essentials.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Explore Sugargoo Spreadsheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-200 text-foreground-700 font-semibold text-sm rounded-full hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Homepage
                <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>

          <nav className="mt-10 pt-8 border-t border-background-200/60">
            <p className="text-xs text-foreground-400 mb-4 flex flex-wrap gap-x-3 gap-y-1">
              <span>Related:</span>
              <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors">Sugargoo Spreadsheet Home</Link>
              <span className="text-foreground-300">·</span>
              <a href="#categories" className="text-primary-600 hover:text-primary-700 transition-colors">Browse Categories</a>
              <span className="text-foreground-300">·</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 transition-colors">Visit Main Website</a>
            </p>
          </nav>
        </div>
      </article>

      <Footer />
    </main>
  );
}